/**
 * GearReviewsSection Component
 * Displays gear reviews from Digital DJ Tips within the Gear & Equipment section
 * Gold/Amber theme to match the main card
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Star, Search, ChevronDown, ExternalLink, Loader2, X, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Review categories for filtering
const REVIEW_CATEGORIES = [
  { id: 'all', name: 'All Gear' },
  { id: 'controller', name: 'Controllers' },
  { id: 'turntable', name: 'Turntables' },
  { id: 'mixer', name: 'Mixers' },
  { id: 'headphone', name: 'Headphones' },
  { id: 'monitor', name: 'Monitors' },
  { id: 'speaker', name: 'Speakers' },
  { id: 'software', name: 'Software' },
  { id: 'interface', name: 'Audio Interfaces' },
  { id: 'accessory', name: 'Accessories' }
];

// Fallback image SVG
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250'%3E%3Crect fill='%231a1525' width='400' height='250'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial,sans-serif' font-size='18' fill='%23fbbf24'%3EGear Review%3C/text%3E%3C/svg%3E";

// Star Rating Component for community ratings
const CommunityStarRating = ({ rating, onRate, size = 16, readOnly = false, totalRatings = 0 }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div 
        style={{ display: 'flex', gap: '2px' }}
        onMouseLeave={() => !readOnly && setHoverRating(0)}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!readOnly && onRate) onRate(star);
            }}
            onMouseEnter={() => !readOnly && setHoverRating(star)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '1px',
              cursor: readOnly ? 'default' : 'pointer'
            }}
            disabled={readOnly}
          >
            <Star
              size={size}
              fill={(hoverRating || rating) >= star ? '#00ffff' : 'transparent'}
              stroke="#00ffff"
              strokeWidth={1.5}
            />
          </button>
        ))}
      </div>
      {totalRatings > 0 && (
        <span style={{ 
          fontSize: '11px', 
          color: 'rgba(0, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          gap: '3px'
        }}>
          <Users size={10} />
          {totalRatings}
        </span>
      )}
    </div>
  );
};

// Rate Gear Modal Component
const RateGearModal = ({ gear, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [ownsGear, setOwnsGear] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) {
      setError('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ rating, reviewText, ownsGear });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '16px',
          border: '2px solid #00ffff',
          maxWidth: '450px',
          width: '100%',
          padding: '25px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ 
          color: '#00ffff', 
          marginBottom: '8px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          fontSize: '1.3rem'
        }}>
          <Star size={24} fill="#00ffff" />
          Rate This Gear
        </h2>
        <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
          BeatSeek Community Rating
        </p>
        
        <div style={{ 
          background: 'rgba(0, 0, 0, 0.3)', 
          borderRadius: '8px', 
          padding: '12px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#fff', fontSize: '1rem', margin: '0 0 4px 0' }}>
            {gear?.title || 'Unknown Gear'}
          </h3>
          <p style={{ color: '#666', fontSize: '12px', margin: 0 }}>
            {gear?.source || 'Digital DJ Tips'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '10px' }}>
              YOUR RATING *
            </label>
            <CommunityStarRating 
              rating={rating} 
              onRate={setRating}
              size={32}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              YOUR REVIEW (optional)
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience with this gear..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                background: '#2f3136',
                border: '1px solid #40444b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '14px'
            }}>
              <input
                type="checkbox"
                checked={ownsGear}
                onChange={(e) => setOwnsGear(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: '#00ffff' }}
              />
              I own this gear
            </label>
          </div>

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '10px 15px',
              marginBottom: '15px',
              color: '#ef4444',
              fontSize: '13px'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                background: 'transparent',
                border: '1px solid #666',
                borderRadius: '8px',
                color: '#888',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !rating}
              style={{
                flex: 2,
                padding: '12px',
                background: rating ? '#00ffff' : 'rgba(0, 255, 255, 0.3)',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontWeight: '600',
                cursor: rating ? 'pointer' : 'not-allowed',
                fontSize: '14px'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const GearReviewsSection = ({ isExpanded, onClose, isInline = false, onShareToDiscord }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredReview, setHoveredReview] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [communityRatings, setCommunityRatings] = useState({});
  const [ratingModalGear, setRatingModalGear] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/news/feed?force_refresh=false`);
      const data = await response.json();
      
      // Filter to only gear reviews
      const gearReviews = (data.articles || data || []).filter(
        article => article.source_category === 'gear_reviews' || 
                   article.source?.toLowerCase().includes('review')
      );
      
      setReviews(gearReviews);
      
      // Fetch community ratings for each gear
      gearReviews.forEach(async (review) => {
        const gearId = `scraped_${review.id}`;
        try {
          const ratingRes = await fetch(`${API}/api/gear/ratings/${gearId}`);
          if (ratingRes.ok) {
            const ratingData = await ratingRes.json();
            setCommunityRatings(prev => ({
              ...prev,
              [review.id]: {
                average: ratingData.average_rating || 0,
                total: ratingData.total_ratings || 0
              }
            }));
          }
        } catch (err) {
          // Silently fail for rating fetch
        }
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRateGear = async (review, ratingData) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to rate gear');
      return;
    }
    
    const gearId = `scraped_${review.id}`;
    const response = await fetch(`${API}/api/gear/rating`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        gear_id: gearId,
        gear_name: review.title,
        gear_brand: review.source || 'Digital DJ Tips',
        rating: ratingData.rating,
        review_text: ratingData.reviewText || null,
        owns_gear: ratingData.ownsGear || false,
        image_url: review.image || null,
        gear_category: selectedCategory !== 'all' ? selectedCategory : 'other',
        source_url: review.url || null  // Include the original review URL
      })
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Failed to submit rating');
    }
    
    // Update local state
    const ratingRes = await fetch(`${API}/api/gear/ratings/${gearId}`);
    if (ratingRes.ok) {
      const ratingInfo = await ratingRes.json();
      setCommunityRatings(prev => ({
        ...prev,
        [review.id]: {
          average: ratingInfo.average_rating || 0,
          total: ratingInfo.total_ratings || 0
        }
      }));
    }
  };

  useEffect(() => {
    if (isExpanded || isInline) {
      fetchReviews();
    }
  }, [isExpanded, isInline, fetchReviews]);

  // Filter reviews based on search and category
  const filteredReviews = reviews.filter(review => {
    const matchesSearch = !searchTerm || 
      review.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      review.title?.toLowerCase().includes(selectedCategory) ||
      review.description?.toLowerCase().includes(selectedCategory);
    
    return matchesSearch && matchesCategory;
  });

  // Render star rating
  const renderRating = (rating) => {
    if (!rating) return null;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={14}
            fill={i < fullStars ? '#fbbf24' : (i === fullStars && hasHalfStar ? '#fbbf24' : 'transparent')}
            stroke={i < fullStars || (i === fullStars && hasHalfStar) ? '#fbbf24' : 'rgba(255,255,255,0.3)'}
          />
        ))}
        <span style={{ marginLeft: '0.5rem', color: '#fbbf24', fontSize: '0.8rem', fontWeight: '600' }}>
          {rating}/5
        </span>
      </div>
    );
  };

  // Handle image error
  const handleImageError = (reviewId) => {
    setImageErrors(prev => ({ ...prev, [reviewId]: true }));
  };

  if (!isExpanded && !isInline) return null;

  // Container style depends on inline vs overlay mode
  const containerStyle = isInline ? {
    padding: '2rem',
    color: '#fff'
  } : {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.9)',
    backdropFilter: 'blur(10px)',
    zIndex: 1000,
    overflow: 'auto',
    padding: '2rem'
  };

  return (
    <div 
      data-testid="gear-reviews-section"
      style={containerStyle}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header - Only show when not inline (overlay mode) */}
        {!isInline && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <div>
              <h2 style={{ 
                fontSize: '1.8rem', 
                fontWeight: '700', 
                color: '#fff',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <Star size={28} fill="#fbbf24" stroke="#fbbf24" />
                Gear Reviews
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                Expert reviews from Digital DJ Tips
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="close-reviews-btn"
              style={{
                background: 'rgba(251, 191, 36, 0.2)',
                border: '1px solid rgba(251, 191, 36, 0.4)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fbbf24'
              }}
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Search and Filters - Gold Theme */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          {/* Search */}
          <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '1rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#fbbf24'
              }} 
            />
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="reviews-search-input"
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Category Filter */}
          <div style={{ position: 'relative' }}>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              data-testid="reviews-category-select"
              style={{
                padding: '0.75rem 2.5rem 0.75rem 1rem',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.9rem',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                minWidth: '150px'
              }}
            >
              {REVIEW_CATEGORIES.map(cat => (
                <option 
                  key={cat.id} 
                  value={cat.id}
                  style={{ background: '#1a1a2e', color: '#fff' }}
                >
                  {cat.name}
                </option>
              ))}
            </select>
            <ChevronDown 
              size={16} 
              style={{ 
                position: 'absolute', 
                right: '0.75rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#fbbf24',
                pointerEvents: 'none'
              }} 
            />
          </div>
        </div>

        {/* Reviews Grid */}
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '4rem',
            color: '#fbbf24'
          }}>
            <Loader2 size={32} className="animate-spin" style={{ marginRight: '1rem' }} />
            Loading reviews...
          </div>
        ) : filteredReviews.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '4rem',
            color: 'rgba(255,255,255,0.5)'
          }}>
            <Star size={48} style={{ marginBottom: '1rem', opacity: 0.3, color: '#fbbf24' }} />
            <p>No reviews found matching your criteria</p>
            <button
              onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: 'rgba(251, 191, 36, 0.2)',
                border: '1px solid rgba(251, 191, 36, 0.4)',
                borderRadius: '6px',
                color: '#fbbf24',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {filteredReviews.map((review) => (
              <a
                key={review.id}
                href={review.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`review-card-${review.id}`}
                onMouseEnter={() => setHoveredReview(review.id)}
                onMouseLeave={() => setHoveredReview(null)}
                style={{
                  display: 'block',
                  background: hoveredReview === review.id 
                    ? 'rgba(251, 191, 36, 0.15)'
                    : 'rgba(251, 191, 36, 0.05)',
                  borderRadius: '12px',
                  border: hoveredReview === review.id
                    ? '1px solid rgba(251, 191, 36, 0.6)'
                    : '1px solid rgba(251, 191, 36, 0.2)',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  transform: hoveredReview === review.id ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: hoveredReview === review.id ? '0 8px 24px rgba(251, 191, 36, 0.2)' : 'none'
                }}
              >
                {/* Image */}
                <div style={{
                  height: '180px',
                  background: 'rgba(0,0,0,0.3)',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <img
                    src={imageErrors[review.id] ? FALLBACK_IMAGE : (review.image || FALLBACK_IMAGE)}
                    alt={review.title}
                    onError={() => handleImageError(review.id)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      transform: hoveredReview === review.id ? 'scale(1.05)' : 'scale(1)'
                    }}
                  />
                </div>

                {/* Content */}
                <div style={{ padding: '1rem' }}>
                  {/* Rating */}
                  {review.rating && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      {renderRating(review.rating)}
                    </div>
                  )}
                  
                  {/* BeatSeek Community Rating */}
                  {communityRatings[review.id] && communityRatings[review.id].total > 0 && (
                    <div style={{ 
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 10px',
                      background: 'rgba(0, 255, 255, 0.1)',
                      borderRadius: '6px',
                      border: '1px solid rgba(0, 255, 255, 0.2)'
                    }}>
                      <span style={{ color: '#00ffff', fontSize: '10px', fontWeight: '600' }}>BEATSEEK</span>
                      <CommunityStarRating 
                        rating={communityRatings[review.id].average} 
                        totalRatings={communityRatings[review.id].total}
                        readOnly 
                        size={14}
                      />
                    </div>
                  )}

                  {/* Title */}
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#fff',
                    margin: '0 0 0.5rem 0',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {review.title}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.6)',
                    margin: '0 0 1rem 0',
                    lineHeight: '1.5',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {review.description}
                  </p>

                  {/* Source & Link */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid rgba(251, 191, 36, 0.15)'
                  }}>
                    <span style={{
                      fontSize: '0.75rem',
                      color: 'rgba(255,255,255,0.4)'
                    }}>
                      {review.source}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* Rate Gear Button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setRatingModalGear(review);
                        }}
                        title="Rate this gear"
                        data-testid={`rate-gear-btn-${review.id}`}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(0, 255, 255, 0.2)',
                          border: '1px solid #00ffff',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: '#00ffff',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}
                      >
                        <Star size={12} />
                        Rate
                      </button>
                      {/* Discord Share Button */}
                      {onShareToDiscord && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onShareToDiscord({
                              title: review.title,
                              name: review.title,
                              description: review.description,
                              brand: review.source,
                              url: review.url,
                              image: review.image
                            });
                          }}
                          title="Share to Discord"
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(88, 101, 242, 0.2)',
                            border: '1px solid #5865F2',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 71 55" fill="#5865F2">
                            <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                          </svg>
                        </button>
                      )}
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        fontSize: '0.75rem',
                        color: '#fbbf24',
                        fontWeight: '500'
                      }}>
                        Read Review <ExternalLink size={12} />
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Rating Modal */}
        {ratingModalGear && (
          <RateGearModal
            gear={ratingModalGear}
            onClose={() => setRatingModalGear(null)}
            onSubmit={(data) => handleRateGear(ratingModalGear, data)}
          />
        )}
      </div>
    </div>
  );
};

export default GearReviewsSection;
